import { User } from 'src/app/core/auth/user.model';
import { CreativeLibraryVariant } from './creative-library-variant.model';
import { CreativeLibrary } from './creative-library.model';
import { KeyLabel } from './key-label.model';
import { Targeting } from './targeting.model';
import { ActiveInactiveArchived } from 'src/app/shared/enums/active-inactive-archived.enum';
import { UserRole } from 'src/app/core/auth/user-role.model';
import { Publisher } from './publisher.model';

export class LineItem {
  public id: string;
  public name: string;
  public active?: boolean;
  public cpm?: number;
  public ioCpm?: number;
  public agencyFee?: number;
  public createdBy?: User;
  public associationCreativeType?: AssociationCreativeType;
  public disableLowPacing?: boolean;
  public domain?: string;
  public endDate?: string;
  public grossBudget?: any;
  public frequencyCappings?: FrequencyCapping[];
  public iabCategory?: string;
  public impressionsGoal?: number;
  public lineItemCreatives?: Array<LineItemCreative>;
  public lineItemDisplayCreatives?: Array<LineItemImageCreative>;
  public lineItemType?: string;
  public numericId?: number;
  public order?: Order;
  public availableProductSets?: string[];
  public selectedProductSets?: string[];
  public pacing?: string;
  public pacingPerHour?: string;
  public startDate?: string;
  public status?: string;
  public targeting?: Targeting;
  public testMode?: boolean;
  public updatedBy?: User;
  public deliveredImpressions?: number;
  public impressionTrackers?: Array<string>;
  public dayTimeActivities?: Array<DayTimeActivities>;
  public iabCategories?: Array<string>;
  public trackerStart: string;
  public trackerFirstQuartile: string;
  public trackerMidpoint: string;
  public trackerThirdQuartile: string;
  public trackerComplete: string;
  public priority: number; //1-5
  // public momentCreatives: MomentCreative[];
  public deliveryStatus?: LineItemDeliveryStatus;
  public buffer?: number;
  public dcoCreatives: any;
  public adFormat: string;
  public impressionCappings: ImpressionsCappings[];
  public healthStatus?: HealthStatus;
}

export class DayTimeActivities {
  /** Weekday: MONDAY..SUNDAY. Always set for weekly and daily (dated) slots. */
  public day: string;
  /** Specific calendar date YYYY-MM-DD. Present only in Daily (dated) mode. */
  public date?: string;
  public restrictedHours: boolean; //If hour restriction is on
  public startHour: number; //0-23
  public endHour: number; //0-23
}

export class ImpressionsCappings {
  id: string;
  publisher: Publisher;
  publisherId: string;
  maxDeliverPercent: number;
}

export class FrequencyCapping {
  public id: string;
  public fcDuration: number;
  public fcLimit: number;
  public fcTimeUnit: string;
}

export class LineItemCreative {
  public endDate: string;
  public startDate: string;
  public variants: CreativeLibraryVariant;
  public creative: CreativeLibrary;
  availableProductSets?: string[];
  selectedProductSets?: string[];
  public weight: number;
  public id?: string;
  //weight min 0, max 100.
}

export class LineItemImageCreative {
  public endDate: string;
  public startDate: string;
  public creative: CreativeLibrary;
  public weight: number;
  public id?: string;
  //weight min 0, max 100.
}

export class Order {
  public advertiser: Advertiser;
  public name: string;
  public trafficker: string;
  public createdBy: User;
  public lineItemsCnt: number;
  public startDate: string;
  public endDate: string;
  public notes: string;
  public poNumber: string;
  public id: string;
  public agency: string;
  public salesPerson: string;
  public status: OrderStatus;
  public secondaryId: string;
  public owners: TeamMember[];
  public team: {
    members: TeamMember[];
  };
  public test: boolean;
  public salesforceId: string;
  public dropboxLink: string;
  public salesSource: SalesSourceType;
  public saleDate: string;
  public agencyName: string;
  public brandName: string;
  public currency: string;
  public buffer: number;
  public statusesCounts: [];
  public grossBudget: number;
  public impressionsGoal: number;
  public enableImpressionsGoalsPerMonth: boolean;
  public monthlyImpressionsGoals: MonthlyImpressionsGoals[];
  public discussions: Discussion[];
  public billingIoNumber: string;
  public billingContactName: string;
  public billingEmail: string;
  public billingPhone: string;
  public ioFiles: any;
  public deliveryStatus: LineItemDeliveryStatus;
  public deliveredBudget: number;
  public deliveredImpressions: number;
  public deliveredScans: number;
  public deliveredCompletions: number;
  public deliveredScansPct: number;
  public deliveredImpressionsPct: number;
  public deliveredCompletionsPct: number;
  public deliveredBudgetPct: number;
  public creatives: CreativeLibrary[];
  public timezone: string;
  public healthStatus?: HealthStatus;
}

export type HealthStatus = 'HEALTHY' | 'WARNING' | 'ERROR' | null;

export class MonthlyImpressionsGoals {
  public month: string;
  public impressionsGoal: number;
}

export class Advertiser {
  id: string;
  name: string;
  status: ActiveInactiveArchived;
  secondaryId: string;
  companyName: string;
  contactName: string;
  phone: string;
  email: string;
  websiteUrl: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
  advertiserKrakenId: string;
  orders: Order[];
}

export class TeamMember {
  id: string;
  name: string;
  username: string;
  email: string;
  network: string;
  roles: UserRole[];
}

export class Discussion {
  id: string;
  title: string;
  postsCount: number;
  unreadPosts: number;
  lastPost: DiscussionPost;
  posts?: DiscussionPost[];
  discussionParticipants: User[];
  createdBy: User;
  updatedBy: User;
  createdOn: Date;
  updatedOn: Date;
}

export class DiscussionPost {
  id: any;
  text: string;
  attachments?: any;
  createdBy: User;
  createdOn?: any;
}

export enum SalesSourceType {
  AGENCY = 'AGENCY',
  ADVERTISER = 'ADVERTISER',
  BRAND_NAME = 'BRAND_NAME'
}

export const SalesSourceTypeLabels = {
  AGENCY: 'Agency',
  ADVERTISER: 'Advertiser',
  BRAND_NAME: 'Brand name'
};

export const SalesSourceTypeKL = () =>
  Object.keys(SalesSourceType).map((key) => new KeyLabel(<SalesSourceType>key, SalesSourceTypeLabels[key]));

export enum AssociationCreativeType {
  STANDARD = 'STANDARD',
  DCO = 'DCO'
}

export const AssociationCreativeTypeLabels = {
  STANDARD: 'Standard',
  DCO: 'DCO'
};

export enum CreativeType {
  MEDIAFILE = 'MEDIAFILE',
  VAST = 'VAST',
  DISPLAY = 'DISPLAY',
  PAUSE_AD = 'PAUSE_AD',
  SLATE = 'SLATE'
}

export const CreativeTypeLabels = {
  MEDIAFILE: 'MediaFile',
  VAST: 'Vast',
  DISPLAY: 'Display',
  PAUSE_AD: 'Pause Ad',
  SLATE: 'Slate'
};

export const CreativeTypeLabelsStandard = {
  MEDIAFILE: 'CTV',
  VAST: 'Vast'
};

export enum VastType {
  URL = 'URL',
  XML = 'XML'
}
export const VastTypeLabels = {
  URL: 'URL',
  XML: 'XML'
};

export enum AdFormatType {
  DIRECT_STANDARD = 'DIRECT_STANDARD',
  DIRECT_ENHANCED = 'DIRECT_ENHANCED',
  DIRECT_SQUEEZEBACK = 'DIRECT_SQUEEZEBACK',
  INTERACTIVE_LINEAR_AD = 'INTERACTIVE_LINEAR_AD',
  NON_LINEAR_FULL_SCREEN_PAUSE_AD = 'NON_LINEAR_FULL_SCREEN_PAUSE_AD',
  NON_LINEAR_PARTIAL_SCREEN_PAUSE_AD = 'NON_LINEAR_PARTIAL_SCREEN_PAUSE_AD',
  CTV_SLATE = 'CTV_SLATE'
}
export const AdFormatTypeLabels = {
  DIRECT_STANDARD: 'Direct Standard',
  DIRECT_ENHANCED: 'Direct Enhanced',
  DIRECT_SQUEEZEBACK: 'Direct Squeezeback',
  INTERACTIVE_LINEAR_AD: 'Interactive Linear Ad',
  NON_LINEAR_FULL_SCREEN_PAUSE_AD: 'Non-Linear - Full Screen Pause Ad',
  NON_LINEAR_PARTIAL_SCREEN_PAUSE_AD: 'Non-Linear - Partial Screen Pause Ad',
  CTV_SLATE: 'CTV Slate'
};

export enum ExecutionType {
  BREAKAWAY = 'BREAKAWAY',
  BRANDED_CANVAS = 'BRANDED_CANVAS',
  BENTO = 'BENTO',
  ENHANCED = 'ENHANCED',
  FLIPBOOK = 'FLIPBOOK',
  INTERACTIVE = 'INTERACTIVE',
  MIRAGE = 'MIRAGE',
  NARRATIVE = 'NARRATIVE',
  NON_LINEAR_FULL_SCREEN_PAUSE_AD = 'NON_LINEAR_FULL_SCREEN_PAUSE_AD',
  NON_LINEAR_PARTIAL_SCREEN_PAUSE_AD = 'NON_LINEAR_PARTIAL_SCREEN_PAUSE_AD',
  RUNWAY = 'RUNWAY',
  SIDEKICK = 'SIDEKICK',
  SLATE = 'SLATE',
  SQUEEZEBACK = 'SQUEEZEBACK',
  SPLITCARD = 'SPLITCARD',
  STANDARD = 'STANDARD',
  TILES = 'TILES',
  WHEEL = 'WHEEL',
  WRAPPER = 'WRAPPER'
}

export const ExecutionTypeLabels = {
  BREAKAWAY: 'Breakaway',
  BRANDED_CANVAS: 'Branded Canvas',
  BENTO: 'Bento',
  ENHANCED: 'Enhanced',
  FLIPBOOK: 'Flipbook',
  INTERACTIVE: 'Interactive',
  MIRAGE: 'Mirage',
  NARRATIVE: 'Narrative',
  NON_LINEAR_FULL_SCREEN_PAUSE_AD: 'Non-Linear - Full Screen Pause Ad',
  NON_LINEAR_PARTIAL_SCREEN_PAUSE_AD: 'Non-Linear - Partial Screen Pause Ad',
  RUNWAY: 'Runway',
  SIDEKICK: 'Sidekick',
  SLATE: 'Slates',
  SQUEEZEBACK: 'Squeezeback',
  SPLITCARD: 'Splitcard',
  STANDARD: 'Standard',
  TILES: 'Tiles',
  WHEEL: 'Wheel',
  WRAPPER: 'Wrapper'
};

export const ExecutionTypeStandard = [ExecutionType.NARRATIVE, ExecutionType.STANDARD];

export const ExecutionTypeVast = [
  ExecutionType.BRANDED_CANVAS,
  ExecutionType.FLIPBOOK,
  ExecutionType.MIRAGE,
  ExecutionType.NARRATIVE,
  ExecutionType.SPLITCARD,
  ExecutionType.STANDARD,
  ExecutionType.TILES
];

export const ExecutionTypeVastKL = () =>
  ExecutionTypeVast.map((key) => new KeyLabel(<ExecutionType>key, ExecutionTypeLabels[key]));

export const ExecutionTypeEnhanced = [
  ExecutionType.BENTO,
  ExecutionType.BRANDED_CANVAS,
  ExecutionType.FLIPBOOK,
  ExecutionType.INTERACTIVE,
  ExecutionType.MIRAGE,
  ExecutionType.NARRATIVE,
  ExecutionType.RUNWAY,
  ExecutionType.SPLITCARD,
  ExecutionType.TILES,
  ExecutionType.WHEEL
];

export const ExecutionTypeDynamic = [
  ExecutionType.BRANDED_CANVAS,
  ExecutionType.FLIPBOOK,
  ExecutionType.MIRAGE,
  ExecutionType.NARRATIVE,
  ExecutionType.SPLITCARD,
  ExecutionType.TILES
];

export const ExecutionTypePause = [
  ExecutionType.NON_LINEAR_FULL_SCREEN_PAUSE_AD,
  ExecutionType.NON_LINEAR_PARTIAL_SCREEN_PAUSE_AD
];

export const ExecutionTypeSlate = [ExecutionType.SLATE];

export const ExecutionTypeDynamicAndEnhancedKL = () =>
  //list is extended to support old creative executions in db
  [
    ExecutionType.BENTO,
    ExecutionType.BRANDED_CANVAS,
    ExecutionType.ENHANCED,
    ExecutionType.FLIPBOOK,
    ExecutionType.INTERACTIVE,
    ExecutionType.MIRAGE,
    ExecutionType.NARRATIVE,
    ExecutionType.RUNWAY,
    ExecutionType.SPLITCARD,
    ExecutionType.STANDARD,
    ExecutionType.TILES,
    ExecutionType.WHEEL
  ].map((key) => new KeyLabel(<ExecutionType>key, ExecutionTypeLabels[key]));

export const ExecutionTypeSqueezeback = [ExecutionType.SQUEEZEBACK];

export const ExecutionTypeSqueezebackKL = () =>
  //list is extended to support old squeezeback executions in db
  [ExecutionType.BREAKAWAY, ExecutionType.SIDEKICK, ExecutionType.SQUEEZEBACK, ExecutionType.WRAPPER].map(
    (key) => new KeyLabel(<ExecutionType>key, ExecutionTypeLabels[key])
  );

export const ExecutionTypePauseKL = () =>
  //list is extended to support old squeezeback executions in db
  [...ExecutionTypePause].map((key) => new KeyLabel(<ExecutionType>key, ExecutionTypeLabels[key]));

export const ExecutionTypeSlateKL = () =>
  [...ExecutionTypeSlate].map((key) => new KeyLabel(<ExecutionType>key, ExecutionTypeLabels[key]));

export const AssociationCreativeTypeKL = () =>
  Object.keys(AssociationCreativeType).map(
    (key) => new KeyLabel(<AssociationCreativeType>key, AssociationCreativeTypeLabels[key])
  );

export const CreativeTypeKL = () =>
  Object.keys(CreativeType).map((key) => new KeyLabel(<CreativeType>key, CreativeTypeLabels[key]));

export const CreativeTypeKLNoDisplay = () =>
  Object.keys(CreativeType)
    .map((key) => new KeyLabel(<CreativeType>key, CreativeTypeLabels[key]))
    .filter((kl) => kl.key !== CreativeType.DISPLAY);

export const VastTypeKL = () => Object.keys(VastType).map((key) => new KeyLabel(<VastType>key, VastTypeLabels[key]));

export const AdFormatTypeKL = () =>
  Object.keys(AdFormatType).map((key) => new KeyLabel(<AdFormatType>key, AdFormatTypeLabels[key]));

export const ExecutionTypeKL = () =>
  Object.keys(ExecutionType).map((key) => new KeyLabel(<ExecutionType>key, ExecutionTypeLabels[key]));

export enum LineItemType {
  DIRECT_IN_POD = 'DIRECT_IN_POD',
  INTERACTIVE = 'INTERACTIVE',
  KARGO_TEST = 'KARGO_TEST',
  DIRECT_SQUEEZEBACK = 'DIRECT_SQUEEZEBACK',
  DIRECT_PAUSE_AD = 'DIRECT_PAUSE_AD',
  DIRECT_SLATE = 'DIRECT_SLATE'
}

export const LineItemTypeLabels = {
  DIRECT_IN_POD: 'Direct In-pod',
  INTERACTIVE: 'Interactive',
  KARGO_TEST: 'Kargo Test',
  DIRECT_SQUEEZEBACK: 'Direct Squeezeback',
  DIRECT_PAUSE_AD: 'Direct Pause Ad',
  DIRECT_SLATE: 'Direct Slate'
};

export const LineItemTypeKL = () =>
  Object.keys(LineItemType).map((key) => new KeyLabel(<LineItemType>key, LineItemTypeLabels[key]));

export enum ForecastType {
  DIRECT = 'DIRECT',
  PROGRAMMATIC = 'PROGRAMMATIC',
  MOMENTS_OVERLAYS = 'MOMENTS_OVERLAYS',
  PAUSE_ADS = 'PAUSE_ADS',
  INTERACTIVE_LINEAR_AD = 'INTERACTIVE_LINEAR_AD'
}

export const ForecastTypeLabels = {
  DIRECT: 'Direct',
  PROGRAMMATIC: 'Programmatic',
  MOMENTS_OVERLAYS: 'Squeezeback Overlays',
  PAUSE_ADS: 'Pause Ads',
  INTERACTIVE_LINEAR_AD: 'Interactive Linear Ad'
};

export const ForecastTypeKL = () =>
  Object.keys(ForecastType).map((key) => new KeyLabel(<ForecastType>key, ForecastTypeLabels[key]));

export enum PacingType {
  EVENLY = 'EVENLY',
  FRONT_LOADED = 'FRONT_LOADED',
  ASAP = 'ASAP'
}

export const PacingTypeLabels = {
  EVENLY: 'Evenly',
  FRONT_LOADED: 'Frontloaded',
  ASAP: 'Asap'
};

export class SchedulerModel {
  public day: string;
  public hour: number[];
}

export class OrdersStatusBox {
  count: number;
  status: string;
}

export enum OrderStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  ARCHIVED = 'ARCHIVED'
}

export const OrderStatusLabels = {
  ACTIVE: 'Active',
  INACTIVE: 'Paused',
  ARCHIVED: 'Archived',
  WARNING: 'Warning',
  ERROR: 'Error'
};

export enum OrderStatusColorMap {
  ACTIVE = 'green',
  INACTIVE = 'lightgray',
  ARCHIVED = 'gray',
  WARNING = 'orange',
  ERROR = 'orange'
}

export const OrderStatusKL = () =>
  Object.keys(OrderStatus).map((key) => new KeyLabel(<OrderStatus>key, OrderStatusLabels[key]));

export class LineItemStatusBox {
  count: number;
  status: string;
}

export enum LineItemDeliveryStatus {
  DELIVERING = 'DELIVERING',
  GOAL_MET = 'GOAL_MET',
  COMPLETED = 'COMPLETED',
  READY = 'READY'
}

export const LineItemDeliveryStatusLabels = {
  DELIVERING: 'Delivering',
  GOAL_MET: 'Goal met',
  COMPLETED: 'Completed',
  READY: 'Ready'
};

export enum LineItemDeliveryStatusColorMap {
  DELIVERING = 'ds-delivering',
  GOAL_MET = 'ds-goal-met',
  COMPLETED = 'ds-completed',
  READY = 'ds-ready'
}

export const LineItemDeliveryStatusKL = () =>
  Object.keys(LineItemDeliveryStatus).map(
    (key) => new KeyLabel(<LineItemDeliveryStatus>key, LineItemDeliveryStatusLabels[key])
  );

export enum LineItemStatus {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
  ARCHIVED = 'ARCHIVED'
}

export const LineItemStatusLabels = {
  ACTIVE: 'Active',
  INACTIVE: 'Paused',
  ARCHIVED: 'Archived'
};

export enum LineItemStatusColorMap {
  ACTIVE = 'green',
  INACTIVE = 'lightgray',
  ARCHIVED = 'gray'
}

export const LineItemStatusKL = () =>
  Object.keys(LineItemStatus).map((key) => new KeyLabel(<LineItemStatus>key, LineItemStatusLabels[key]));

export enum ThirdPartyNameEnum {
  DCM = 'DCM',
  SIZMEK = 'SIZMEK'
}

export const ThirdPartyNameLabels = {
  DCM: 'DCM',
  SIZMEK: 'Sizmek'
};

export const ThirdPartyNameKL = () =>
  Object.keys(ThirdPartyNameEnum).map((key) => new KeyLabel(<ThirdPartyNameEnum>key, ThirdPartyNameLabels[key]));
